// public-reader/PublicReaderApp.tsx — root for story.emberhollow.ai.
//
// This is the surface served from `story.emberhollow.ai`. The same Vite
// bundle that powers the player surface (www) and the admin portal
// (admin); main.tsx branches to this tree when the hostname matches.
//
// Routing here is path-only — no react-router. The reader has exactly one
// page right now (`/s/<slug>`). Everything else 404s gracefully. As Phase 5
// adds the public catalogue, a small switch grows here.
//
// Auth-awareness: the reader doesn't require a session, but if the visitor
// already has a bearer cookie (from a prior www.* signin in the same
// browser) we show "Your bookshelf →" linking back to www; otherwise a
// "Sign in" link. The reader itself never hosts account / billing /
// settings — those redirect to www.

import { useEffect, useMemo, useState } from "react";
import ReaderRoute from "./components/ReaderRoute";
import PublicFooter from "./components/PublicFooter";
import {
  detectBrowserLanguage,
  loadAnonymousLanguage,
  saveAnonymousLanguage,
} from "../i18n/detectLanguage";

const WWW_URL = "https://www.emberhollow.ai";

/**
 * Parse `/s/<slug>` out of the current pathname. Returns null when the
 * path isn't a reader path. Defensive about trailing slashes + multiple
 * segments — `/s/abc` is canonical, `/s/abc/` is OK, `/s/abc/anything`
 * isn't (we don't have per-chapter URLs yet; sidebar nav stays on the
 * single page).
 */
function parseReaderPath(pathname: string): { slug: string } | null {
  const m = pathname.match(/^\/s\/([A-Za-z0-9]+)\/?$/);
  if (!m) return null;
  return { slug: m[1]! };
}

export default function PublicReaderApp(): JSX.Element {
  const [pathname, setPathname] = useState<string>(
    typeof window !== "undefined" ? window.location.pathname : "/",
  );

  // Listen for back/forward navigation. Inside the public reader we use
  // history.pushState when the user follows lineage links so the back
  // button works naturally.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = () => setPathname(window.location.pathname);
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, []);

  // Phase A i18n — anonymous-visitor language detection. If no value is
  // stored in localStorage["ember.lang"] yet, detect from navigator and
  // persist. Phase A doesn't yet render translations on the reader (that
  // lights up in Phase C with the on-the-fly translation pipeline) — this
  // is purely scaffolding so Phase C can read the value without doing
  // its own detection. Best-effort and silent.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (loadAnonymousLanguage() !== null) return;
    const detected = detectBrowserLanguage();
    if (detected) saveAnonymousLanguage(detected);
  }, []);

  const route = useMemo(() => parseReaderPath(pathname), [pathname]);

  // Landing page when the visitor lands on /, /s/, or any non-reader path.
  // Keep it minimal — public-catalogue browsing is a Phase 5 concern.
  if (!route) {
    return <LandingPage />;
  }

  return <ReaderRoute slug={route.slug} />;
}

/**
 * Minimal landing page shown on story.emberhollow.ai/ (no slug yet) or any
 * unrecognised path. Funnels visitors to www. The shape is intentionally
 * unbranded — most traffic to story.* comes via a /s/ link.
 */
function LandingPage(): JSX.Element {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        background:
          "radial-gradient(ellipse at top, #1a1410 0%, #0d0907 60%, #050302 100%)",
        color: "#e8d9b8",
        fontFamily:
          "'Cormorant Garamond', 'EB Garamond', 'Iowan Old Style', 'Palatino Linotype', Palatino, Georgia, serif",
        overflowY: "auto",
      }}
    >
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: 32,
        }}
      >
        <div
          style={{
            fontSize: 13,
            letterSpacing: "0.32em",
            textTransform: "uppercase",
            color: "#8a7355",
            marginBottom: 24,
          }}
        >
          Ember Hollow · Stories
        </div>
        <h1
          style={{
            margin: "0 0 12px",
            fontSize: "2.6rem",
            fontWeight: 500,
            color: "#d4a574",
            textAlign: "center",
          }}
        >
          Each playthrough becomes a book.
        </h1>
        <p
          style={{
            margin: "0 0 32px",
            fontSize: "1.05rem",
            lineHeight: 1.6,
            color: "#cdbfa3",
            maxWidth: 560,
            textAlign: "center",
            fontStyle: "italic",
          }}
        >
          Shared books live here. Land on a link to read; play your own at
          www.emberhollow.ai.
        </p>
        <a
          href={WWW_URL}
          style={{
            padding: "10px 22px",
            border: "1px solid rgba(212,165,116,0.45)",
            color: "#d4a574",
            textDecoration: "none",
            fontFamily: "inherit",
            fontSize: "0.92rem",
            letterSpacing: "0.06em",
            borderRadius: 2,
            background: "transparent",
          }}
        >
          Play at www.emberhollow.ai →
        </a>
      </div>
      <PublicFooter
        accent="#d4a574"
        inkMuted="#8a7355"
        paperEdge="rgba(212,165,116,0.18)"
        paper="rgba(20,14,10,0.55)"
      />
    </div>
  );
}
